import { useState } from "react";

import { formatDistanceToNow } from "date-fns";
import { AlertCircle, Mail, X } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useEmailBounceStatus } from "@/hooks/useEmailBounceStatus";


export const EmailBounceNotice = () => {
  const [dismissed, setDismissed] = useState(false);
  const { data: bounceInfo, isLoading } = useEmailBounceStatus();

  // Don't show if loading, no bounce, or already dismissed
  if (isLoading || !bounceInfo?.has_bounce || dismissed) {
    return null;
  }

  const getMessage = () => {
    switch (bounceInfo.bounce_type) {
      case 'hard':
        return {
          title: "Email Address Issue",
          description: "Your email address is bouncing. This usually means the email address is invalid or no longer exists. Please update your email address in your profile settings to continue receiving important notifications.",
          variant: "destructive" as const,
        };
      case 'complaint':
        return {
          title: "Email Marked as Spam",
          description: "Our emails were marked as spam from your email address. We've stopped sending emails to protect our sender reputation. If this was a mistake, please update your email preferences.",
          variant: "destructive" as const,
        };
      case 'soft':
        return {
          title: "Email Delivery Issue",
          description: "We're having temporary trouble delivering emails to your address. This might be due to a full inbox or temporary server issue. We'll keep trying, but you may want to check your email settings.",
          variant: "default" as const,
        };
      default:
        return null;
    }
  };

  const message = getMessage();
  if (!message) return null;

  const timeAgo = bounceInfo.bounced_at 
    ? formatDistanceToNow(new Date(bounceInfo.bounced_at), { addSuffix: true })
    : 'recently';

  return (
    <Alert variant={message.variant} className="relative mb-4">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle className="flex items-center gap-2">
        <Mail className="h-4 w-4" />
        {message.title}
      </AlertTitle>
      <AlertDescription className="mt-2">
        {message.description}
        {bounceInfo.bounce_reason && (
          <div className="mt-2 text-sm opacity-80">
            Reason: {bounceInfo.bounce_reason}
          </div>
        )}
        <div className="mt-1 text-xs opacity-70">
          Detected {timeAgo}
        </div>
      </AlertDescription>
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 h-6 w-6"
        onClick={() => setDismissed(true)}
      >
        <X className="h-4 w-4" />
      </Button>
    </Alert>
  );
};
